import { assertUnreachable, isDefined, Mutable } from '@evorait/pace-core';
import { AnswerStatus, initialAnswerStatusState } from '../types/answer-status';
import { Answers } from '../types/answers';
import { Checkpoint } from '../types/checkpoint';
import { CheckpointDependencies, Checkpoints } from '../types/checkpoints';
import { Choice, CHOICE_TYPE, Choices } from '../types/choice';
import { ContentId } from '../types/content-block';
import { ContentBlocks } from '../types/content-blocks';
import { CONTENT_TYPE, ContentChild } from '../types/content-child';
import { DatePerformed } from '../types/date-performed';
import { DefectiveComponentReplacement } from '../types/defective-component-replacement';
import { DiscrepancyLog } from '../types/discrepancy-log';
import { FormFilters, FormFiltersType } from '../types/form-filter';
import { FormTypeFilters } from '../types/form-type-filters';
import { FormsConstants } from '../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE, FunctionalElement } from '../types/functional-element';
import { FunctionalElements } from '../types/functional-elements';
import { MaterialSerialNumber } from '../types/material-serial-number';
import { AstoriaOption, isCosimaOption } from '../types/option';
import { ParticipantCopyId } from '../types/participant-copy';
import { RADIO_OPTION_TYPE } from '../types/radio-option';
import { RadioOptions } from '../types/radio-options';
import {
    REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION,
    ReplacementOrLastProcedureDate,
} from '../types/replacement-or-last-procedure-date';
import { isSignature, Signature } from '../types/signature';
import { StandardFunctionalElement } from '../types/standard-functional-element';
import { ValueFunctionalElement } from '../types/value-functional-element';
import { FormUtilities } from '../utilities/form-utilities';
import FORMTYPE = FormsConstants.FORMTYPE;

function addAnswerStatus(first: AnswerStatus, second: AnswerStatus): AnswerStatus {
    return {
        answeredCheckpoints: first.answeredCheckpoints + second.answeredCheckpoints,
        answeredCheckpointsNotOK: first.answeredCheckpointsNotOK + second.answeredCheckpointsNotOK,
        totalCheckpoints: first.totalCheckpoints + second.totalCheckpoints,
        answeredFunctionalElements: first.answeredFunctionalElements + second.answeredFunctionalElements,
        totalFunctionalElements: first.totalFunctionalElements + second.totalFunctionalElements,
    };
}

export function isAnyFilterActive(typeFilters: FormTypeFilters, formFilters: FormFilters): boolean {
    const typeFiltersActive = Object.entries(typeFilters).some(([, filter]) => filter.active && filter.visible);
    const formFiltersActive = Object.entries(formFilters).some(([, filter]) => filter.visible);
    return typeFiltersActive || formFiltersActive;
}

export function isFunctionalElementAnswered(f: FunctionalElement, options: RadioOptions): boolean {
    // Astoria handling of controlling options
    const controllingOptions = options[f.controllingCheckpoint];
    const selectedOption =
        typeof controllingOptions !== 'undefined' ? controllingOptions.find((o) => o.selected) : undefined;
    const controllingOptionsNoOrNaSelected =
        typeof selectedOption !== 'undefined' &&
        [RADIO_OPTION_TYPE.NO, RADIO_OPTION_TYPE.NA].includes(selectedOption.optionType);

    // choices are only available in cosima.
    const choices = f.choices ?? {};
    const choicesValues = Object.values(choices);
    const selectedChoice = choicesValues.find((c) => c?.selected);
    const choicesNoOrNaSelected =
        isDefined(selectedChoice) && [CHOICE_TYPE.CHOICE_NEGATIVE, CHOICE_TYPE.CHOICE_NA].includes(selectedChoice.type);
    const hasSelectedChoice = choicesValues.filter(isDefined).some((c) => c.selected);
    switch (f.type) {
        case FUNCTIONAL_ELEMENT_TYPE.STANDARD:
            return controllingOptionsNoOrNaSelected || hasSelectedChoice;
        case FUNCTIONAL_ELEMENT_TYPE.DROP_DOWN_OPTIONS:
            return controllingOptionsNoOrNaSelected || hasSelectedChoice;
        case FUNCTIONAL_ELEMENT_TYPE.REMARK: {
            const remarkFunctionalElement = f as StandardFunctionalElement;
            // all cosima functional elements have remarks.
            return (hasSelectedChoice || choicesValues.length === 0) && !!remarkFunctionalElement.remarks?.length;
        }
        case FUNCTIONAL_ELEMENT_TYPE.DATE:
            // if yes is selected, then ensure, that also a date exists on the functional element.
            return selectedChoice?.type === CHOICE_TYPE.CHOICE_NA || (f as DatePerformed).date.length > 0;
        case FUNCTIONAL_ELEMENT_TYPE.DATE_PERFORMED:
            return selectedOption?.optionType === RADIO_OPTION_TYPE.NA || (f as DatePerformed).date.length > 0;
        case FUNCTIONAL_ELEMENT_TYPE.DEFECTIVE_COMPONENT_REPLACEMENT: {
            const defectiveComponentReplacement = f as DefectiveComponentReplacement;

            return (
                selectedChoice?.type === CHOICE_TYPE.CHOICE_NA ||
                selectedChoice?.type === CHOICE_TYPE.CHOICE_POSITIVE ||
                (defectiveComponentReplacement.repairTime.length > 0 &&
                    defectiveComponentReplacement.swapInComponent.matNo.length > 0 &&
                    defectiveComponentReplacement.swapInComponent.serialNo.length > 0 &&
                    defectiveComponentReplacement.swapOutComponent.matNo.length > 0 &&
                    defectiveComponentReplacement.swapOutComponent.serialNo.length > 0)
            );
        }
        case FUNCTIONAL_ELEMENT_TYPE.LAST_PROCEDURE_DATE:
        case FUNCTIONAL_ELEMENT_TYPE.REPLACEMENT_DATE:
            return (
                controllingOptionsNoOrNaSelected ||
                ((f as ReplacementOrLastProcedureDate).selected !== REPLACEMENT_OR_LAST_PROCEDURE_DATE_SELECTION.NONE &&
                    (f as ReplacementOrLastProcedureDate).date.length > 0)
            );
        case FUNCTIONAL_ELEMENT_TYPE.DISCREPANCY_LOG:
            return controllingOptionsNoOrNaSelected || (f as DiscrepancyLog).value.length > 0;
        case FUNCTIONAL_ELEMENT_TYPE.COMPONENT:
            return (
                selectedChoice?.type === CHOICE_TYPE.CHOICE_NA ||
                ((f as MaterialSerialNumber).materialValue.length > 0 &&
                    (f as MaterialSerialNumber).serialValue.length > 0)
            );
        case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO:
            return (
                selectedOption?.optionType === RADIO_OPTION_TYPE.NA ||
                ((f as MaterialSerialNumber).materialValue.length > 0 &&
                    (f as MaterialSerialNumber).serialValue.length > 0)
            );
        case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO_WITH_DESC:
            return (
                controllingOptionsNoOrNaSelected ||
                ((f as MaterialSerialNumber).materialValue.length > 0 &&
                    (f as MaterialSerialNumber).serialValue.length > 0 &&
                    (f as MaterialSerialNumber).descriptionValue.length > 0)
            );
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER: {
            const customerSignature = f as Signature;
            return (
                (selectedOption && selectedOption.optionType === RADIO_OPTION_TYPE.NA) ||
                (customerSignature.firstName.length > 0 &&
                    customerSignature.lastName.length > 0 &&
                    (!customerSignature.emailRequired || customerSignature.email.length > 0) &&
                    customerSignature.signature.length > 0)
            );
        }
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE: {
            const employeeSignature = f as Signature;
            return (
                (selectedOption && selectedOption.optionType === RADIO_OPTION_TYPE.NA) ||
                (employeeSignature.firstName.length > 0 &&
                    employeeSignature.lastName.length > 0 &&
                    employeeSignature.signature.length > 0)
            );
        }
        case FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE:
        case FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE:
            return (
                selectedChoice?.type === CHOICE_TYPE.CHOICE_NA ||
                ((f as Signature).firstName.length > 0 &&
                    (f as Signature).lastName.length > 0 &&
                    (f as Signature).signature.length > 0)
            );
        case FUNCTIONAL_ELEMENT_TYPE.VALUE:
        case FUNCTIONAL_ELEMENT_TYPE.VALUE_UNIT: {
            const valueFunctionalElement = f as ValueFunctionalElement;

            // in cosima we need to react on choices and not controlling checkpoints
            const noOrNaSelected = isDefined(valueFunctionalElement.choices)
                ? choicesNoOrNaSelected
                : controllingOptionsNoOrNaSelected;

            return noOrNaSelected || valueFunctionalElement.value.length > 0;
        }
        case FUNCTIONAL_ELEMENT_TYPE.OPTION:
            return typeof (f as AstoriaOption).selected !== 'undefined';
        case FUNCTIONAL_ELEMENT_TYPE.OPTION_PRESENT:
            return isDefined(selectedChoice);
        // These are hidden in the prologue and not visible to the user, so we ignore them for the answer status
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION:
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER_SYSTEM_CONDITION:
        // Will never be added when parsing the XML, so it does not really matter what we return here

        case FUNCTIONAL_ELEMENT_TYPE.WORKSTEPS_PERFORMED:
            return true;
        default:
            return assertUnreachable(f.type);
    }
}

function isCheckpointAnswered(c: Checkpoint, options: RadioOptions): { answered: boolean; answeredNotOK: boolean } {
    const checkpointOptions = options[c.xmlId];
    if (typeof checkpointOptions === 'undefined') {
        throw new Error(`AnswerUtilities: isCheckpointAnswered: No options found for checkpoint '${c.xmlId}'`);
    }

    const selectedOption = checkpointOptions.find((o) => o.selected);

    return {
        answered: typeof selectedOption !== 'undefined',
        answeredNotOK: typeof selectedOption !== 'undefined' && selectedOption.optionType === RADIO_OPTION_TYPE.NO,
    };
}

export function isCosimaFunctionalElementVisible(
    functionalElement: FunctionalElement,
    typeFilters: FormTypeFilters,
    formFilters: FormFilters,
    elementType: FormsConstants.ELEMENT_TYPE | undefined,
): boolean {
    if (!isAnyFilterActive(typeFilters, formFilters)) {
        return true;
    }

    let visible = isDefined(elementType) && typeFilters[elementType].visible;
    if (!visible && formFilters[FormFiltersType.INCOMPLETE_ELEMENTS].visible) {
        const choicesValues = Object.values(functionalElement.choices ?? {});
        const selectedChoice = choicesValues.find((c) => c?.selected);

        const functionalElementAnswered = isFunctionalElementAnswered(functionalElement, {});
        const choiceSelected = choicesValues.length === 0 ? true : typeof selectedChoice !== 'undefined';
        visible = !functionalElementAnswered || !choiceSelected;
    }

    if (!visible && formFilters[FormFiltersType.NOTOK_ELEMENTS].visible) {
        const choicesValues = Object.values(functionalElement.choices ?? {});
        const selectedChoice = choicesValues.find((c) => c?.selected);

        visible = choicesValues.length !== 0 && selectedChoice?.type === CHOICE_TYPE.CHOICE_NEGATIVE;
    }

    return visible;
}

function isCosimaPositiveChoiceSelectedInMaster(choices: Choices): boolean {
    const selectedChoice = Object.values(choices)
        .filter(isDefined)
        .find((c) => c.selectedInMaster);

    return selectedChoice?.type === CHOICE_TYPE.CHOICE_POSITIVE;
}

function getCosimaSelectedChoice(choices: Choices): Choice | undefined {
    return Object.values(choices)
        .filter(isDefined)
        .find((c) => c.selected);
}

export function isAstoriaFunctionalElementVisible(
    functionalElement: FunctionalElement,
    elementType: FormsConstants.ELEMENT_TYPE | undefined,
    typeFilters: FormTypeFilters,
    formFilters: FormFilters,
    options: RadioOptions,
): boolean {
    if (!isAnyFilterActive(typeFilters, formFilters)) {
        return true;
    }

    let visible = isDefined(elementType) && typeFilters[elementType].visible;
    if (!visible && formFilters[FormFiltersType.INCOMPLETE_ELEMENTS].visible) {
        const controllingOptions = options[functionalElement.controllingCheckpoint];
        const functionalElementAnswered = isFunctionalElementAnswered(functionalElement, options);
        const checkpointAnswered =
            typeof controllingOptions === 'undefined'
                ? true
                : typeof controllingOptions.find((o) => o.selected) !== 'undefined';
        // only hide functionalElement, if the checkpoint is either not available or already selected
        visible = !functionalElementAnswered || !checkpointAnswered;
    }

    if (!visible && formFilters[FormFiltersType.NOTOK_ELEMENTS].visible) {
        const controllingOptions = options[functionalElement.controllingCheckpoint];
        const selectedOption =
            typeof controllingOptions !== 'undefined' ? controllingOptions.find((o) => o.selected) : undefined;

        visible = typeof selectedOption !== 'undefined' && selectedOption.optionType === RADIO_OPTION_TYPE.NO;
    }

    return visible;
}

function getAnsweredFunctionalElementCount(
    contentChildren: ContentChild[],
    functionalElements: FunctionalElements,
    options: RadioOptions,
): number {
    return contentChildren
        .map((c) => isFunctionalElementAnswered(functionalElements[c.id], options))
        .reduce((accu, isAnswered) => accu + (isAnswered ? 1 : 0), 0);
}

function getAnsweredCheckpoints(answeredFunctionalElements: Array<{ answered: boolean; answeredNotOK: boolean }>): {
    answered: number;
    answeredNotOK: number;
} {
    return answeredFunctionalElements.reduce(
        (accu, isAnswered) => ({
            answered: accu.answered + (isAnswered.answered ? 1 : 0),
            answeredNotOK: accu.answeredNotOK + (isAnswered.answeredNotOK ? 1 : 0),
        }),
        { answered: 0, answeredNotOK: 0 },
    );
}

function getContentAnswer(
    selectedParticipant: ParticipantCopyId,
    contentId: ContentId,
    contentBlocks: ContentBlocks,
    checkpoints: Checkpoints,
    options: RadioOptions,
    functionalElements: FunctionalElements,
    dependencies: CheckpointDependencies,
    typeFilters: FormTypeFilters,
    formFilters: FormFilters,
    answers: Mutable<Answers>,
    enableManualIndividualization: boolean,
    formType: FORMTYPE,
): AnswerStatus {
    const contentBlock = contentBlocks[contentId];

    return contentBlock.children
        .filter((c) => c.type === CONTENT_TYPE.CONTENT)
        .map((c) =>
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            AnswerUtilities.getAnswerStatus(
                selectedParticipant,
                c.id,
                contentBlocks,
                checkpoints,
                options,
                functionalElements,
                dependencies,
                typeFilters,
                formFilters,
                answers,
                enableManualIndividualization,
                formType,
            ),
        )
        .reduce((accu, current) => addAnswerStatus(accu, current), { ...initialAnswerStatusState });
}

function getCosimaAnswerStatus(
    selectedParticipant: ParticipantCopyId,
    contentId: ContentId,
    contentBlocks: ContentBlocks,
    checkpoints: Checkpoints,
    options: RadioOptions,
    functionalElements: FunctionalElements,
    dependencies: CheckpointDependencies,
    typeFilters: FormTypeFilters,
    formFilters: FormFilters,
    answers: Mutable<Answers>,
    contentChildren: ContentChild[],
    enableManualIndividualization: boolean,
    formType: FORMTYPE,
): AnswerStatus {
    const contentBlock = contentBlocks[contentId];
    const answeredChoices: Array<{ answered: boolean; answeredNotOK: boolean }> = [];
    const functionalElementChildren = contentChildren.filter((functionalContent) => {
        const functionalElement = functionalElements[functionalContent.id];

        let loadedFromXml = false;
        let positiveChoiceSelectedInMaster = false;

        const choices = functionalElement.choices;

        if (
            !isCosimaOption(functionalElement) &&
            !isCosimaFunctionalElementVisible(
                functionalElement,
                typeFilters,
                formFilters,
                functionalElement.elementType,
            )
        ) {
            return false;
        }

        if (isDefined(choices)) {
            // if the option_present element is not visible, we do not count it as a possible answer
            if (isCosimaOption(functionalElement) && !functionalElement.visible) {
                return false;
            }

            const selectedChoice = getCosimaSelectedChoice(choices);

            // We are always displaying signatures, so we always need to consider them in the answer calculation
            if (
                functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE ||
                functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE
            ) {
                if (Object.values(choices).length > 0) {
                    answeredChoices.push({
                        answered: isDefined(selectedChoice),
                        answeredNotOK:
                            isDefined(selectedChoice) &&
                            !isCosimaOption(functionalElement) &&
                            selectedChoice.type === CHOICE_TYPE.CHOICE_NEGATIVE,
                    });
                }
                return true;
            }

            loadedFromXml = functionalElement.contentLoadedFromXml;
            positiveChoiceSelectedInMaster = isCosimaPositiveChoiceSelectedInMaster(choices);

            // in participant copies we are showing only the checkpoints/choices which were marked as yes
            // this is required for the participant copies
            if (selectedParticipant !== FormsConstants.MASTER_PARTICIPANT_ID && !positiveChoiceSelectedInMaster) {
                return false;
            }

            if (Object.values(choices).length > 0) {
                answeredChoices.push({
                    answered: isDefined(selectedChoice),
                    answeredNotOK:
                        isDefined(selectedChoice) &&
                        !isCosimaOption(functionalElement) &&
                        selectedChoice.type === CHOICE_TYPE.CHOICE_NEGATIVE,
                });
            }

            if (functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.STANDARD || isCosimaOption(functionalElement)) {
                // standard functional elements
                return false;
            }
        }

        // content that was loaded from an interim XML is not displayed in participant copies
        if (selectedParticipant !== FormsConstants.MASTER_PARTICIPANT_ID && loadedFromXml) {
            return false;
        }

        // stuff in master is always visible
        return (
            selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID ||
            // functional elements with controlling checkpoints are only visible if the checkpoint was
            // answered with YES in master
            positiveChoiceSelectedInMaster
        );
    });

    const answeredFunctionalElementCount = getAnsweredFunctionalElementCount(
        functionalElementChildren,
        functionalElements,
        options,
    );

    const answeredCheckpoints = getAnsweredCheckpoints(answeredChoices);

    const contentAnswer = getContentAnswer(
        selectedParticipant,
        contentId,
        contentBlocks,
        checkpoints,
        options,
        functionalElements,
        dependencies,
        typeFilters,
        formFilters,
        answers,
        enableManualIndividualization,
        formType,
    );

    const currentAnswers: AnswerStatus = {
        answeredCheckpoints: contentAnswer.answeredCheckpoints + answeredCheckpoints.answered,
        answeredCheckpointsNotOK: contentAnswer.answeredCheckpointsNotOK + answeredCheckpoints.answeredNotOK,
        totalCheckpoints: contentAnswer.totalCheckpoints + answeredChoices.length,
        answeredFunctionalElements: contentAnswer.answeredFunctionalElements + answeredFunctionalElementCount,
        totalFunctionalElements: contentAnswer.totalFunctionalElements + functionalElementChildren.length,
    };

    answers[contentBlock.id] = currentAnswers;

    return currentAnswers;
}

function getAstoriaAnswerStatus(
    selectedParticipant: ParticipantCopyId,
    contentId: ContentId,
    contentBlocks: ContentBlocks,
    checkpoints: Checkpoints,
    options: RadioOptions,
    functionalElements: FunctionalElements,
    dependencies: CheckpointDependencies,
    typeFilters: FormTypeFilters,
    formFilters: FormFilters,
    answers: Mutable<Answers>,
    contentChildren: ContentChild[],
    enableManualIndividualization: boolean,
    formType: FORMTYPE,
): AnswerStatus {
    const contentBlock = contentBlocks[contentId];
    const checkpointsToBeFiltered: Checkpoint[] = [];
    const functionalElementChildren = contentChildren.filter((functionalContent) => {
        const functionalElement = functionalElements[functionalContent.id];

        // signatures should always be visible
        if (isSignature(functionalElement)) {
            return true;
        }

        // for functional elements without controlling checkpoint we need to check if we display a participant
        // copy. If yes, then we also need to be aware if the content was loaded from XML.
        if (functionalElement.controllingCheckpoint.length === 0) {
            // There is no controlling checkpoint, so only evaluating if other filters are active
            const visibleByFilter = isAstoriaFunctionalElementVisible(
                functionalElement,
                undefined,
                typeFilters,
                formFilters,
                {},
            );
            return (
                visibleByFilter &&
                (selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID ||
                    !functionalElement.contentLoadedFromXml)
            );
        }

        // Now we know that there is a controlling checkpoint. So let's evaluate its answer status!
        const dependency = dependencies[functionalElement.controllingCheckpoint];
        if (
            !isAstoriaFunctionalElementVisible(
                functionalElement,
                dependency?.checkpoint ? checkpoints[dependency.checkpoint].elementType : undefined,
                typeFilters,
                formFilters,
                options,
            )
        ) {
            if (dependency?.checkpoint) {
                checkpointsToBeFiltered.push(checkpoints[dependency.checkpoint]); // existing check is done in filterFunction call
            }
            return false;
        }

        const checkpointOptions = options[functionalElement.controllingCheckpoint];
        if (typeof checkpointOptions === 'undefined') {
            throw new Error(
                `AnswerUtilities: getAnswerStatus: No options found for controlling checkpoint '${functionalElement.controllingCheckpoint}'`,
            );
        }
        const selectedOption = checkpointOptions.find((o) => o.selectedInMaster);

        const loadedFromXml =
            typeof selectedOption !== 'undefined'
                ? // if we have a selected answer in the controlling checkpoint, ensure that it is not loaded from XML
                  selectedOption.contentLoadedFromXml
                : // if we don't have a controlling checkpoint, then just ensure that the data of the functional
                  // element itself was not loaded from XML
                  functionalElement.contentLoadedFromXml;

        const positiveChoiceSelectedInMaster = selectedOption?.optionType === RADIO_OPTION_TYPE.YES;

        // content that was loaded from an interim XML is not displayed in participant copies
        if (selectedParticipant !== FormsConstants.MASTER_PARTICIPANT_ID && loadedFromXml) {
            return false;
        }

        // stuff in master is always visible
        return (
            selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID ||
            // functional elements with controlling checkpoints are only visible if the checkpoint was
            // answered with YES in master
            positiveChoiceSelectedInMaster
        );
    });

    const answeredFunctionalElementCount = getAnsweredFunctionalElementCount(
        functionalElementChildren,
        functionalElements,
        options,
    );

    const checkpointChildren = contentBlock.children
        .filter((content) => content.type === CONTENT_TYPE.CHECKPOINT)
        .filter(
            (checkpointContent) =>
                typeof checkpointsToBeFiltered.find(
                    (filteredCheckpoint) => filteredCheckpoint.id === checkpointContent.id,
                ) === 'undefined',
        )
        .filter((checkpointContent) => {
            const checkpoint = checkpoints[checkpointContent.id];
            const checkpointOptions = options[checkpoint.xmlId];
            if (typeof checkpointOptions === 'undefined') {
                throw new Error(
                    `AnswerUtilities: getAnswerStatus: No options found for checkpoint '${checkpoint.xmlId}'`,
                );
            }
            const selectedOption = checkpointOptions.find((o) => o.selected);
            const loadedFromXml = typeof selectedOption !== 'undefined' && selectedOption.contentLoadedFromXml;
            const yesSelected =
                typeof selectedOption !== 'undefined' && selectedOption.optionType === RADIO_OPTION_TYPE.YES;
            // Check if there is a dependent functional element and if it is a signature
            const dependency = dependencies[checkpoint.xmlId];
            const functionalElement =
                typeof dependency === 'undefined' ? undefined : functionalElements[dependency.functionalElement];
            const hasDependentSignature =
                typeof functionalElement !== 'undefined' &&
                (functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER ||
                    functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE);

            let visibleByFilter = !isAnyFilterActive(typeFilters, formFilters);
            const typeFilterVisible = typeFilters[checkpoint.elementType].visible;
            // standalone checkpoint is completed as soon as an option is selected
            const showIncompleteElement =
                formFilters[FormFiltersType.INCOMPLETE_ELEMENTS].visible && !isDefined(selectedOption);
            const showNotOkElement =
                formFilters[FormFiltersType.NOTOK_ELEMENTS].visible &&
                isDefined(selectedOption) &&
                selectedOption.optionType === RADIO_OPTION_TYPE.NO;

            visibleByFilter = visibleByFilter || typeFilterVisible || showIncompleteElement || showNotOkElement;

            return (
                visibleByFilter &&
                (selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID ||
                    (!loadedFromXml && (yesSelected || hasDependentSignature)))
            );
        });

    const checkpointAnswers = checkpointChildren.map((c) => isCheckpointAnswered(checkpoints[c.id], options));

    const answeredCheckpoints = getAnsweredCheckpoints(checkpointAnswers);

    const contentAnswer = getContentAnswer(
        selectedParticipant,
        contentId,
        contentBlocks,
        checkpoints,
        options,
        functionalElements,
        dependencies,
        typeFilters,
        formFilters,
        answers,
        enableManualIndividualization,
        formType,
    );

    const currentAnswers: AnswerStatus = {
        answeredCheckpoints: contentAnswer.answeredCheckpoints + answeredCheckpoints.answered,
        answeredCheckpointsNotOK: contentAnswer.answeredCheckpointsNotOK + answeredCheckpoints.answeredNotOK,
        totalCheckpoints: contentAnswer.totalCheckpoints + checkpointChildren.length,
        answeredFunctionalElements: contentAnswer.answeredFunctionalElements + answeredFunctionalElementCount,
        totalFunctionalElements: contentAnswer.totalFunctionalElements + functionalElementChildren.length,
    };

    answers[contentBlock.id] = currentAnswers;

    return currentAnswers;
}

export class AnswerUtilities {
    public static getCompleteAnswers(
        selectedParticipant: ParticipantCopyId,
        contentBlocks: ContentBlocks,
        checkpoints: Checkpoints,
        options: RadioOptions,
        functionalElements: FunctionalElements,
        dependencies: CheckpointDependencies,
        typeFilters: FormTypeFilters,
        formFilters: FormFilters,
        enableManualIndividualization: boolean,
        formType: FORMTYPE,
    ): Answers {
        const topLevelContentBlocks = Object.values(contentBlocks).filter((cb) => cb.parentId === -1);
        const answers = {};
        topLevelContentBlocks.forEach((cb) => {
            AnswerUtilities.getAnswerStatus(
                selectedParticipant,
                cb.id,
                contentBlocks,
                checkpoints,
                options,
                functionalElements,
                dependencies,
                typeFilters,
                formFilters,
                answers,
                enableManualIndividualization,
                formType,
            );
        });

        return answers;
    }

    public static getAnswerStatus(
        selectedParticipant: ParticipantCopyId,
        contentId: ContentId,
        contentBlocks: ContentBlocks,
        checkpoints: Checkpoints,
        options: RadioOptions,
        functionalElements: FunctionalElements,
        dependencies: CheckpointDependencies,
        typeFilters: FormTypeFilters,
        formFilters: FormFilters,
        answers: Mutable<Answers>,
        enableManualIndividualization: boolean,
        formType: FORMTYPE,
    ): AnswerStatus {
        const contentBlock = contentBlocks[contentId];

        const filteredContent = contentBlock.children.filter((content) => content.type === CONTENT_TYPE.FUNCTIONAL);

        const isCosimaForm = FormUtilities.isCosimaForm(formType);

        if (isDefined(isCosimaForm) && isCosimaForm) {
            // if a topic is not visible, then return an empty status state reponse
            if (!contentBlock.visible && !enableManualIndividualization) {
                // we are adding the empty entries to the answers, as we need these for the evaluation of the form when submitting
                answers[contentBlock.id] = initialAnswerStatusState;
                return initialAnswerStatusState;
            }

            return getCosimaAnswerStatus(
                selectedParticipant,
                contentId,
                contentBlocks,
                checkpoints,
                options,
                functionalElements,
                dependencies,
                typeFilters,
                formFilters,
                answers,
                filteredContent,
                enableManualIndividualization,
                formType,
            );
        }

        return getAstoriaAnswerStatus(
            selectedParticipant,
            contentId,
            contentBlocks,
            checkpoints,
            options,
            functionalElements,
            dependencies,
            typeFilters,
            formFilters,
            answers,
            filteredContent,
            enableManualIndividualization,
            formType,
        );
    }
}
